import { create } from 'zustand';
import { zhCN } from '../core/i18n/zhCN';
import {
  indexedDbSettingsRepository,
  type SettingsRepository,
} from '../data/repositories/settingsRepository';
import {
  DEFAULT_AUDIO_SETTINGS,
  normalizeVolume,
  type AudioSettings,
} from '../features/settings/domain/audioSettings';
import { audioEngine } from '../infrastructure/audio/audioEngine';
import { useUiStore } from './uiStore';

interface SettingsState {
  audio: AudioSettings;
  status: 'loading' | 'ready';
  panelOpen: boolean;
  actions: {
    hydrate(): Promise<void>;
    openPanel(): void;
    closePanel(): void;
    setMusicVolume(value: number): void;
    setSfxVolume(value: number): void;
  };
}

let repository: SettingsRepository = indexedDbSettingsRepository;
let saveTimer: number | undefined;

function scheduleSave(settings: AudioSettings): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    void repository
      .saveAudio(settings)
      .catch(() => useUiStore.getState().showNotice(zhCN.audio.saveFailed));
  }, 300);
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  audio: DEFAULT_AUDIO_SETTINGS,
  status: 'loading',
  panelOpen: false,
  actions: {
    async hydrate() {
      const audio = await repository.loadAudio().catch(() => DEFAULT_AUDIO_SETTINGS);
      audioEngine.applySettings(audio);
      set({ audio, status: 'ready' });
    },
    openPanel: () => set({ panelOpen: true }),
    closePanel: () => set({ panelOpen: false }),
    setMusicVolume(value) {
      const audio = { ...get().audio, musicVolume: normalizeVolume(value) };
      audioEngine.applySettings(audio);
      set({ audio });
      scheduleSave(audio);
    },
    setSfxVolume(value) {
      const audio = { ...get().audio, sfxVolume: normalizeVolume(value) };
      audioEngine.applySettings(audio);
      set({ audio });
      scheduleSave(audio);
    },
  },
}));

export function setSettingsRepositoryForTests(nextRepository: SettingsRepository): void {
  repository = nextRepository;
}

export function resetSettingsRepository(): void {
  repository = indexedDbSettingsRepository;
  window.clearTimeout(saveTimer);
  saveTimer = undefined;
}
